import { match } from "@codesandbox/states";
import { useResetLocalToRemoteCommand } from "commands/definitions/branch/useResetLocalToRemoteCommand";
import { useToggleBranchCreatorCommand } from "commands/definitions/branch/useToggleBranchCreatorCommand";
import { useToggleBranchPickerCommand } from "commands/definitions/branch/useToggleBranchPickerCommand";
import { useEditorStore } from "components/Editor/useEditorStore";
import { useEnvironmentInterface } from "environment-interface";
import { useContainer } from "features/container/Feature";
import { useEditorFeature, useBranchEditor } from "features/editor/Feature";
import { useSession } from "hooks/api/useSession";
import { useCapabilities } from "hooks/pitcher/useCapabilities";
import { usePermissions } from "hooks/pitcher/usePermissions";
import { hasBranchWriteAccess } from "isomorphic/permission";
import { Button, Dropdown, Icon, Text, Stack, Tooltip } from "prism-react";
import React, { useEffect } from "react";

import { WorkspaceIndication } from "components/WorkspaceIndication";
import { useAnalytics } from "hooks/useAnalytics";
import { useWorkspacesOfBranch } from "hooks/useWorkspacesOfBranch";
import { githubUrl, slugifyBranchName, getPullRequestUrl } from "utils/git";
import { paths } from "utils/router";

import { AnimatedDiv } from "../AnimatedDiv";
import { PullRequestTitle } from "../PullRequestTitle";
import { RenamableTitle } from "../RenamableTitle";
import { TitleMenu } from "../TitleMenu";
import { TitleUI } from "../TitleUI";
import { useTitleState } from "../useTitleState";
import {
  appendBranchIdForContributionBranch,
  removeBranchIdForContributionBranch,
} from "../utils";

const MAX_BRANCH_NAME_LENGTH = 80;

export interface BranchTitleProps {
  isInteractive: boolean;
  maxLength?: number;
}

export const RepositoryTitle: React.FC<BranchTitleProps> = ({
  isInteractive,
  maxLength = MAX_BRANCH_NAME_LENGTH,
}) => {
  const [container] = useContainer();

  return match(
    container,
    {
      DISCONNECTED: () => <RenderContent isInteractive={false} />,
      RECONNECTING: () => <RenderContent isInteractive={false} />,
      DISCONNECTED_RECONNECTING: () => <RenderContent isInteractive={false} />,
      CHANGING_CONNECTION: () => <RenderContent isInteractive={false} />,
      READY: () => (
        <RenderContent isInteractive={isInteractive} maxLength={maxLength} />
      ),
    },
    () => null,
  );
};

const PUBLIC_WORKSPACE = { name: "Public", id: null };
const RenderContent: React.FC<BranchTitleProps> = ({
  isInteractive,
  maxLength = MAX_BRANCH_NAME_LENGTH,
}) => {
  const { navigation } = useEnvironmentInterface();
  const [_, dispatchProject] = useEditorFeature();
  const project = useBranchEditor();
  const pitcherPermissions = usePermissions();
  const pitcherCapabilities = useCapabilities();
  const pullRequest = project.pullRequest;
  const hasPR = !!pullRequest;

  const { id, branch: name, contribution, repo, owner } = project;
  const workspace = project.workspace ?? PUBLIC_WORKSPACE;

  const branchNameWithoutPrefix = removeBranchIdForContributionBranch(
    id,
    name,
    contribution,
  );

  useEffect(() => {
    let prefix = `${name} / ${repo}`;

    if (pullRequest) {
      prefix = `#${pullRequest.number} ${pullRequest.title}`;
    }

    document.title = `${prefix} - CodeSandbox`;
  }, [pullRequest, name, repo]);

  const { setShowRenameForm } = useTitleState();

  const toggleBranchCreator = useToggleBranchCreatorCommand();
  const toggleBranchPicker = useToggleBranchPickerCommand();
  const resetLocalToRemote = useResetLocalToRemoteCommand({
    event_source: "topbar",
  });

  const canRename = !!(
    pitcherCapabilities.git?.renameBranch &&
    pitcherPermissions.git?.renameBranch &&
    !hasPR
  );

  const toggleBranchCreationCommandPalette = () => {
    toggleBranchCreator.perform();
  };

  const toggleBranchPickerCommandPalette = () => {
    toggleBranchPicker.perform();
  };

  const resetLocalToRemoteComandPalette = () => {
    resetLocalToRemote.perform();
  };

  const removeBranchFromCSB = async () => {
    dispatchProject({ type: "REMOVE_BRANCH", branch: name });
  };

  const handleRename = (newName: string) => {
    if (!newName || newName.length > maxLength) return Promise.reject();

    // Contribution branches are identified by the csb-{branchId} prefix
    const newBranchName = appendBranchIdForContributionBranch(
      id,
      slugifyBranchName(newName),
      contribution,
    );

    dispatchProject({
      type: "RENAME_BRANCH",
      newName: newBranchName,
    });

    setShowRenameForm(false);

    return Promise.resolve();
  };

  // Fallback: show the project/repository owner from GitHub
  let ownerName = project.owner;

  if (workspace) {
    // If the repository is scoped to a workspace, show the workspace name
    ownerName = workspace.name;
  }

  return (
    <TitleUI
      type={{ type: "repository", owner: project.owner, repo: project.repo }}
    >
      {hasBranchWriteAccess(project.authorization) && !project.contribution ? (
        <>
          <AnimatedDiv>
            <Tooltip content={`Open workspace dashboard`}>
              <Button
                as="a"
                css={{
                  padding: "$1",
                }}
                href={paths.dashboard.recent(workspace?.id ?? undefined)}
              >
                {ownerName}
              </Button>
            </Tooltip>
          </AnimatedDiv>
          <AnimatedDiv>/</AnimatedDiv>
          <AnimatedDiv>
            <Tooltip content={`Open repository overview`}>
              <Button
                as="a"
                css={{
                  padding: "$1",
                }}
                href={paths.dashboard.branches(
                  owner,
                  repo,
                  workspace?.id ?? null,
                )}
              >
                {repo}
              </Button>
            </Tooltip>
          </AnimatedDiv>
        </>
      ) : (
        <Text truncate>
          {owner}/{repo}
        </Text>
      )}

      {/* Branch Icon */}
      {project.contribution ? (
        <AnimatedDiv
          css={{
            display: "flex",
            alignItems: "center",
            // Hate this but the contribution branch icon
            // looks off next to small case letters
            marginTop: "1px",
            marginLeft: "1px",
          }}
        >
          <Icon size={4} type={"contribution"} />
        </AnimatedDiv>
      ) : (
        <AnimatedDiv
          css={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Icon size={4} type={"branch"} />
        </AnimatedDiv>
      )}

      {hasPR ? (
        <PullRequestTitle
          number={pullRequest.number}
          onClick={() => {
            navigation.openUrl(
              getPullRequestUrl({
                owner,
                repo,
                prNumber: pullRequest.number,
              }),
            );
          }}
          title={pullRequest.title}
        />
      ) : (
        <RenamableTitle
          canRename={canRename}
          displayValue={branchNameWithoutPrefix}
          onClick={toggleBranchPickerCommandPalette}
          onRename={handleRename}
          projectType={project.type}
          value={name}
        />
      )}

      <TitleMenu canRename={canRename} isInteractive={isInteractive}>
        <Menu
          removeBranchFromCodesandbox={
            isInteractive ? removeBranchFromCSB : undefined
          }
          resetLocalToRemoteCommandPalette={
            isInteractive ? resetLocalToRemoteComandPalette : undefined
          }
          toggleBranchCreationCommandPalette={
            isInteractive ? toggleBranchCreationCommandPalette : undefined
          }
          toggleBranchPickerCommandPalette={
            isInteractive ? toggleBranchPickerCommandPalette : undefined
          }
        />
      </TitleMenu>
    </TitleUI>
  );
};

/**
 * @todo this might be better if pure
 */
const Menu: React.FC<{
  toggleBranchPickerCommandPalette?: () => void;
  toggleBranchCreationCommandPalette?: () => void;
  resetLocalToRemoteCommandPalette?: () => void;
  removeBranchFromCodesandbox?: () => void;
}> = ({
  toggleBranchPickerCommandPalette,
  toggleBranchCreationCommandPalette,
  resetLocalToRemoteCommandPalette,
  removeBranchFromCodesandbox,
}) => {
  const { navigation } = useEnvironmentInterface();
  const track = useAnalytics();
  const [, dispatchEditor] = useEditorFeature();
  const { git } = useEditorStore();
  const [session] = useSession();
  const pitcherPermissions = usePermissions();
  const pitcherCapabilities = useCapabilities();

  const currentUser = session.state === "AUTHENTICATED" ? session.user : null;
  const project = useBranchEditor();
  const branchWorkspaces = useWorkspacesOfBranch({
    owner: project.owner,
    repo: project.repo,
    branch: project.branch,
  });

  const {
    allowedCapabilities,
    owner,
    repo,
    branch: name,
    pullRequest,
  } = project;
  const workspace = project.workspace ?? PUBLIC_WORKSPACE;

  return (
    <>
      <Dropdown.Item
        onKeyDown={(e) => {
          // Keydown should not propagate as the command palette is open
          e.stopPropagation();
        }}
        onSelect={(e) => {
          e.preventDefault();

          track("topbar_open_command_palette", { item: "change" });

          // For some reason it can flicker without this. Probably because the mouse
          // click goes through and closes it again before it opens
          setTimeout(() => {
            toggleBranchPickerCommandPalette?.();
          }, 50);
        }}
      >
        Change branch...
      </Dropdown.Item>
      <Dropdown.Item
        disabled={!allowedCapabilities.branch}
        onKeyDown={(e) => {
          // Keydown should not propagate as the command palette is open
          e.stopPropagation();
        }}
        onSelect={(e) => {
          e.preventDefault();

          track("topbar_open_command_palette", { item: "create" });
          toggleBranchCreationCommandPalette?.();
        }}
      >
        Create branch...
      </Dropdown.Item>
      <Dropdown.Item
        disabled={
          !pitcherPermissions.git?.pull ||
          !pitcherCapabilities.git?.pull ||
          !git.status.remote.branch
        }
        onKeyDown={(e) => {
          // Keydown should not propagate as the command palette is open
          e.stopPropagation();
        }}
        onSelect={(e) => {
          e.preventDefault();

          resetLocalToRemoteCommandPalette?.();
        }}
      >
        Reset branch to remote
      </Dropdown.Item>

      {branchWorkspaces && branchWorkspaces.length > 1 ? (
        <>
          <Dropdown.Separator />
          <Dropdown.SubMenu trigger="Change workspace">
            {branchWorkspaces.map((branchWorkspace) => {
              const isCurrentWorkspace = branchWorkspace.id === workspace.id;

              return (
                <Dropdown.Item
                  key={branchWorkspace.id}
                  onClick={() => {
                    dispatchEditor({
                      type: "FETCH_BRANCH",
                      owner,
                      repo,
                      branch: name,
                      workspaceId: branchWorkspace.id,
                      forcePublicWorkspace: branchWorkspace.id === null,
                    });
                  }}
                >
                  <Stack css={{ width: "100%" }} horizontal>
                    <WorkspaceIndication
                      currentUser={currentUser}
                      workspace={branchWorkspace}
                    />

                    {isCurrentWorkspace ? (
                      <Stack css={{ marginLeft: "auto" }}>
                        <Icon size={3} type="check" />
                      </Stack>
                    ) : null}
                  </Stack>
                </Dropdown.Item>
              );
            })}
          </Dropdown.SubMenu>
        </>
      ) : null}
      <Dropdown.Item
        disabled={
          !allowedCapabilities.branch || project.defaultBranchName === name
        }
        onKeyDown={(e) => {
          // Keydown should not propagate as the command palette is open
          e.stopPropagation();
        }}
        onSelect={(e) => {
          e.preventDefault();

          removeBranchFromCodesandbox?.();
        }}
      >
        Remove branch from CodeSandbox
      </Dropdown.Item>
      <Dropdown.Separator />
      <Dropdown.Item
        css={{
          display: "flex",
          gap: "$2",
        }}
        disabled={!allowedCapabilities.branch}
        onSelect={(e) => {
          e.stopPropagation();
          navigation.openUrl(
            paths.dashboard.branches(owner, repo, workspace?.id ?? null),
          );
        }}
      >
        <span>Manage branches</span>
        <Icon size={3} type="externalLink" />
      </Dropdown.Item>
      <Dropdown.Item
        css={{
          display: "flex",
          gap: "$2",
        }}
        disabled={project.contribution}
        onSelect={(e) => {
          e.stopPropagation();
          navigation.openUrl(
            githubUrl.branch({ owner, repo, branchName: project.branch }),
          );
        }}
      >
        <span>Open branch on GitHub</span>
        <Icon size={3} type="externalLink" />
      </Dropdown.Item>
      {pullRequest !== null ? (
        <Dropdown.Item
          css={{
            display: "flex",
            gap: "$2",
          }}
          disabled={project.contribution}
          onSelect={(e) => {
            e.stopPropagation();
            if (!pullRequest) {
              return;
            }

            navigation.openUrl(
              getPullRequestUrl({
                owner,
                repo,
                prNumber: pullRequest.number,
              }),
            );
          }}
        >
          <span>Open PR on GitHub</span>
          <Icon size={3} type="externalLink" />
        </Dropdown.Item>
      ) : null}
    </>
  );
};
